import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../../common/hooks/hooks";
import { TransformedProduct } from "../../../../product/types/productApiTypes";
import { useRemoveFromCompare } from "../../../hooks/useRemoveFromCompare";
import { openProductModal } from "../../../../product/components/common/ProductModal/productModalSlice";
import CompareHeaderCell from "./CompareHeaderCell";
import CompareDataCell from "./CompareDataCell";
import ScrollContainer from "react-indiana-drag-scroll";

export interface CompareRowConfig {
  key: string; // 'price', 'availability', 'description', or taxonomy slug
  type: "static" | "attribute";
  label?: string;
}

interface CompareTableProps {
  products: TransformedProduct[];
}

const CompareTable: React.FC<CompareTableProps> = ({ products }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { handleRemoveFromCompare } = useRemoveFromCompare();
  // Ref to control scroll behavior programmatically if needed later
  const scrollRef = useRef<HTMLElement>(null);

  const handleRemove = (id: number) => {
    handleRemoveFromCompare(id);
  };

  // --- Quick View Logic Update ---
  // If the product is a variation, we want to open the Parent Product in the modal
  // but pre-select the specific attributes of this variation.
  const handleQuickView = (product: TransformedProduct) => {
    if (product.type === "variation" && product.parentId > 0) {
      // Extract attributes from the enriched data available in the compare object.
      // We need to map them to format: { "pa_color": "blue", "pa_size": "large" }
      const attributes: { [key: string]: string } = {};

      if (product.enrichedAttributes) {
        product.enrichedAttributes.forEach((attr) => {
          if (attr.terms && attr.terms.length > 0) {
            attributes[attr.taxonomy] = attr.terms[0].slug;
          }
        });
      }

      // Open modal with Parent ID and pre-selected attributes
      dispatch(
        openProductModal({
          productId: product.parentId,
          initialAttributes: attributes,
        })
      );
    } else {
      // Simple or Variable Parent: Open normally using object syntax
      dispatch(openProductModal({ productId: product.id }));
    }
  };

  // --- Prepare Row Configuration ---
  const rows: CompareRowConfig[] = useMemo(() => {
    // Base static rows
    const staticRows: CompareRowConfig[] = [
      {
        key: "price",
        type: "static",
        label: t("comparePage.rows.price", "Price"),
      },
      {
        key: "availability",
        type: "static",
        label: t("comparePage.rows.availability", "Availability"),
      },
      {
        key: "description",
        type: "static",
        label: t("comparePage.rows.description", "Description"),
      },
    ];

    // Extract unique attribute taxonomies from ALL products
    const allAttributeTaxonomies = Array.from(
      new Set(
        products.flatMap((p) =>
          p.enrichedAttributes.map((attr) => attr.taxonomy)
        )
      )
    );

    // Map taxonomies to row configs
    const attributeRows: CompareRowConfig[] = allAttributeTaxonomies.map(
      (taxonomy) => {
        const sourceProduct = products.find((p) =>
          p.enrichedAttributes.some((a) => a.taxonomy === taxonomy)
        );
        const attributeName =
          sourceProduct?.enrichedAttributes.find((a) => a.taxonomy === taxonomy)
            ?.name || taxonomy;

        return {
          key: taxonomy,
          type: "attribute",
          label: attributeName,
        };
      }
    );

    return [...staticRows, ...attributeRows];
  }, [products, t]);

  return (
    // Wrapped in ScrollContainer for drag-to-scroll functionality on desktop
    <ScrollContainer
      className="compare-scroll-wrapper"
      innerRef={scrollRef}
      hideScrollbars={false} // Keep scrollbars visible for accessibility/visual cue
      draggingClassName="is-dragging" // Applies this class to the wrapper while dragging
    >
      <div className="compare-table">
        {/* --- Header Row --- */}
        <div className="compare-row compare-row--header">
          {/* Empty Sticky Corner */}
          <div className="compare-cell compare-cell--label compare-cell--sticky"></div>

          {/* Product Headers */}
          {products.map((product) => (
            <div
              key={product.id}
              className="compare-cell compare-cell--product"
            >
              <CompareHeaderCell
                product={product}
                onRemove={handleRemove}
                onQuickView={handleQuickView}
              />
            </div>
          ))}
        </div>

        {/* --- Data Rows --- */}
        {rows.map((row) => (
          <div className="compare-row" key={row.key}>
            {/* Sticky Label */}
            <div className="compare-cell compare-cell--label compare-cell--sticky">
              {row.label}
            </div>

            {/* Product Cells */}
            {products.map((product) => (
              <div
                className="compare-cell compare-cell--product"
                key={product.id}
              >
                <CompareDataCell row={row} product={product} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </ScrollContainer>
  );
};

export default CompareTable;
